import React from 'react';

/**
 * WorkspaceHeader — Consistent page header across all authenticated workspace pages.
 *
 * Props:
 *  - title        (string)  Page title
 *  - description  (string)  Short page description
 *  - actions      (node)    Right-aligned action buttons/controls
 *  - children     (node)    Optional below-header content (filters, tabs, search)
 *  - icon         (Component) Optional Phosphor icon to show before title
 *  - badge        (node)    Optional badge next to title (e.g. count)
 */
export const WorkspaceHeader = ({
  title,
  description,
  actions,
  children,
  icon: Icon,
  badge,
}) => {
  return (
    <div
      className="border-b border-[var(--border-subtle)] bg-[var(--surface-0)]"
      data-testid="workspace-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 py-5">
        {/* Title row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              {Icon && (
                <div className="w-8 h-8 rounded-lg bg-[rgba(99,102,241,0.08)] border border-[rgba(99,102,241,0.12)] flex items-center justify-center flex-shrink-0">
                  <Icon size={16} weight="duotone" className="text-[#818CF8]" />
                </div>
              )}
              <h1
                className="text-lg font-semibold text-[var(--text-primary)] tracking-tight truncate"
                style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}
                data-testid="workspace-header-title"
              >
                {title}
              </h1>
              {badge}
            </div>
            {description && (
              <p className="text-[13px] text-[var(--text-muted)] mt-1 leading-relaxed">
                {description}
              </p>
            )}
          </div>
          {actions && (
            <div className="flex items-center gap-2 flex-shrink-0" data-testid="workspace-header-actions">
              {actions}
            </div>
          )}
        </div>

        {/* Optional sub-header content (filters, tabs, search) */}
        {children && (
          <div className="mt-4" data-testid="workspace-header-sub">
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * WorkspaceContent — Max-width content container with consistent padding.
 */
export const WorkspaceContent = ({ children, className = '', fullWidth = false }) => {
  return (
    <div
      className={`${fullWidth ? '' : 'max-w-[1400px] mx-auto'} px-6 py-6 ${className}`}
      data-testid="workspace-content"
    >
      {children}
    </div>
  );
};

/**
 * EmptyState — Consistent empty state pattern across the platform.
 *
 * Props:
 *  - icon         (Component) Phosphor icon
 *  - title        (string)
 *  - description  (string)
 *  - action       (node)     Primary action button
 *  - secondaryAction (node)  Optional secondary action
 */
export const EmptyState = ({ icon: Icon, title, description, action, secondaryAction }) => {
  return (
    <div
      className="flex flex-col items-center justify-center py-16 px-6"
      data-testid="empty-state"
    >
      {Icon && (
        <div className="w-14 h-14 rounded-xl bg-[var(--surface-2)] border border-[var(--border-default)] flex items-center justify-center mb-4">
          <Icon size={24} weight="duotone" className="text-[var(--text-muted)]" />
        </div>
      )}
      <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1.5">
        {title}
      </h3>
      {description && (
        <p className="text-[13px] text-[var(--text-muted)] text-center max-w-sm mb-5 leading-relaxed">
          {description}
        </p>
      )}
      {(action || secondaryAction) && (
        <div className="flex items-center gap-3">
          {secondaryAction}
          {action}
        </div>
      )}
    </div>
  );
};

export default WorkspaceHeader;
